import React, { useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  HStack,
  Checkbox,
  Select,
  NumberInput,
  NumberInputField,
  Text,
} from '@chakra-ui/react';
import { exportDataAdvanced } from '../../services/exportService';

interface ExportModalProps<T extends Record<string, any>> {
  isOpen: boolean;
  onClose: () => void;
  data: T[];
  title?: string;
  defaultFilename?: string;
  // Optional pre-selected fields; if not provided we'll infer from data[0]
  availableFields?: string[];
  defaultSelectedFields?: string[];
}

const ExportModal = <T extends Record<string, any>>({
  isOpen,
  onClose,
  data,
  title = 'Export Data',
  defaultFilename = 'export',
  availableFields,
  defaultSelectedFields,
}: ExportModalProps<T>) => {
  const [filename, setFilename] = useState(defaultFilename);
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [chunkSize, setChunkSize] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const inferredFields = useMemo(() => availableFields || (data && data[0] ? Object.keys(data[0]) : []), [availableFields, data]);
  const [selectedFields, setSelectedFields] = useState<string[]>(defaultSelectedFields || inferredFields);

  // Filter by createdAt if present
  const filteredData = useMemo(() => {
    if (!startDate && !endDate) return data;
    const start = startDate ? new Date(startDate).getTime() : undefined;
    const end = endDate ? new Date(endDate).getTime() : undefined;
    return data.filter((item) => {
      const raw = (item as any).createdAt;
      let ts: number | undefined;
      if (raw && typeof (raw as any).toDate === 'function') ts = (raw as any).toDate().getTime();
      else if (typeof raw === 'string' || typeof raw === 'number') ts = new Date(raw as any).getTime();
      if (!ts) return true; // keep if no createdAt present
      if (start && ts < start) return false;
      if (end) {
        // include entire end day
        const endDay = new Date(end);
        endDay.setHours(23, 59, 59, 999);
        if (ts > endDay.getTime()) return false;
      }
      return true;
    });
  }, [data, startDate, endDate]);

  const toggleField = (field: string) => {
    setSelectedFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]));
  };

  const handleExport = () => {
    exportDataAdvanced(filteredData, {
      filename,
      includeHeaders,
      fields: selectedFields,
      format,
      chunkSize,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Filename</FormLabel>
              <Input value={filename} onChange={(e) => setFilename(e.target.value)} placeholder="export" />
            </FormControl>

            <HStack spacing={4} align="flex-end">
              <FormControl>
                <FormLabel>Start Date (optional)</FormLabel>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>End Date (optional)</FormLabel>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </FormControl>
              <FormControl w="180px">
                <FormLabel>Format</FormLabel>
                <Select value={format} onChange={(e) => setFormat(e.target.value as 'csv' | 'json')}>
                  <option value="csv">CSV</option>
                  <option value="json">JSON</option>
                </Select>
              </FormControl>
            </HStack>

            <HStack spacing={4} align="flex-end">
              <FormControl>
                <Checkbox isChecked={includeHeaders} onChange={(e) => setIncludeHeaders(e.target.checked)}>
                  Include headers (CSV)
                </Checkbox>
              </FormControl>
              <FormControl w="220px">
                <FormLabel>Chunk size (rows, optional)</FormLabel>
                <NumberInput value={chunkSize || undefined} onChange={(_, v) => setChunkSize(Number.isFinite(v) && v > 0 ? v : undefined)} min={0} step={1000}>
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Fields</FormLabel>
              <HStack wrap="wrap" spacing={3}>
                {inferredFields.map((field) => (
                  <Checkbox key={field} isChecked={selectedFields.includes(field)} onChange={() => toggleField(field)}>
                    <Text fontSize="sm">{field}</Text>
                  </Checkbox>
                ))}
              </HStack>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={handleExport} isDisabled={filteredData.length === 0 || selectedFields.length === 0}>Export</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ExportModal;
